/**
 * The canvas surface. Shared by the full-width graph app and the right-panel
 * local graph; everything scope-specific arrives as props.
 */
import { useEffect, useRef, useState } from "react";
import { useGraph, type GraphScope } from "@/store/graph";
import { GraphController } from "./controller";
import { openGraphNode } from "./openNode";
import type { GraphData, GraphNode } from "./types";
import s from "./Graph.module.css";

export interface GraphCanvasHandle {
  controller: GraphController | null;
}

interface Props {
  scope: GraphScope;
  data: GraphData;
  currentItemId: string | null;
  onController?: (c: GraphController | null) => void;
  onPlaybackChange?: () => void;
  /** Rendered over the canvas, e.g. the playback bar. */
  children?: React.ReactNode;
}

export function GraphCanvas({ scope, data, currentItemId, onController, onPlaybackChange, children }: Props) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const controllerRef = useRef<GraphController | null>(null);
  const [hovered, setHovered] = useState<GraphNode | null>(null);
  const onPlaybackChangeRef = useRef(onPlaybackChange);
  onPlaybackChangeRef.current = onPlaybackChange;
  const settings = useGraph((st) => (scope === "global" ? st.global : st.local));

  useEffect(() => {
    const host = hostRef.current;
    const canvas = canvasRef.current;
    if (!host || !canvas) return;
    const controller = new GraphController(scope, {
      onOpen: openGraphNode,
      onHover: setHovered,
      onPlaybackChange: () => onPlaybackChangeRef.current?.(),
    });
    controllerRef.current = controller;
    controller.attach(canvas, host);
    onController?.(controller);
    return () => {
      controller.detach();
      controllerRef.current = null;
      onController?.(null);
    };
  }, [scope, onController]);

  useEffect(() => {
    controllerRef.current?.setDisplay(settings.display);
  }, [settings.display]);

  useEffect(() => {
    controllerRef.current?.setForces(settings.forces);
  }, [settings.forces]);

  useEffect(() => {
    controllerRef.current?.setData(data);
    controllerRef.current?.setCurrentItem(currentItemId);
  }, [data]);

  useEffect(() => {
    controllerRef.current?.setCurrentItem(currentItemId);
  }, [currentItemId]);

  return (
    <div className={s.host} ref={hostRef} data-graph-canvas>
      <canvas ref={canvasRef} className={s.canvas} />
      {hovered ? (
        <div className={s.hoverLabel} data-graph-hover>
          {hovered.label}
        </div>
      ) : null}
      {children}
    </div>
  );
}
